/**
 * Order in which boat sources are normalized and processed during an upload.
 *
 * The upload pipeline is serial for normalization and runs only a couple of
 * `2_process_and_execute.py` jobs at a time, so a full fleet day finishes over many minutes.
 * Ordering the *selected* sources puts the boats we actually look at first on disk and in the
 * reports, instead of whoever happens to sort first alphabetically (AUS, BRA, CAN, ...).
 *
 * Anything not listed keeps running — it just runs after the listed teams, in alphabetical order.
 * This is purely an ordering preference: no source is skipped, and the set of work is unchanged.
 */
export const UPLOAD_SOURCE_PRIORITY: readonly string[] = ['GER', 'NZL', 'AUS', 'ESP', 'SWE'];

/** Position in UPLOAD_SOURCE_PRIORITY, or one past the end for unlisted sources. */
function priorityRank(sourceName: string | null | undefined): number {
  const name = String(sourceName ?? '').trim().toUpperCase();
  const index = UPLOAD_SOURCE_PRIORITY.indexOf(name);
  return index === -1 ? UPLOAD_SOURCE_PRIORITY.length : index;
}

/** Comparator: priority teams in listed order, then everything else alphabetically. */
export function compareUploadSourcePriority(
  a: string | null | undefined,
  b: string | null | undefined
): number {
  const rankA = priorityRank(a);
  const rankB = priorityRank(b);
  if (rankA !== rankB) return rankA - rankB;
  return String(a ?? '').localeCompare(String(b ?? ''));
}

/** Sort source names (already filtered to the user's selection) into upload order. */
export function sortSourcesByUploadPriority(sourceNames: Iterable<string>): string[] {
  return [...sourceNames].sort(compareUploadSourcePriority);
}

/** Comparator for per-dataset work: priority source first, then oldest date first. */
export function compareUploadTaskPriority(
  a: { source_name?: string | null; date?: string | null },
  b: { source_name?: string | null; date?: string | null }
): number {
  const bySource = compareUploadSourcePriority(a.source_name, b.source_name);
  if (bySource !== 0) return bySource;
  return String(a.date ?? '').localeCompare(String(b.date ?? ''));
}
